import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalibrationItem, calibrationConfig, mandatoryCalibrationPassed } from '../config/calibration';
import { averageFrameDifference, sampleVideoFrame } from '../utils/cameraSetup';

interface VideoCalibrationOptions {
  mode: 'laptop' | 'mobile';
  required?: boolean;
  connected?: boolean;
  microphoneTrack?: MediaStreamTrack;
}

interface FaceDetectorLike {
  detect: (video: HTMLVideoElement) => Promise<Array<{ boundingBox: DOMRectReadOnly }>>;
}

type FaceDetectorConstructor = new (options?: { fastMode?: boolean; maxDetectedFaces?: number }) => FaceDetectorLike;

function item(key: string, label: string, mandatory: boolean, guidance: string): CalibrationItem {
  return { key, label, mandatory, guidance, state: 'checking' };
}

export function useVideoCalibration(
  videoRef: RefObject<HTMLVideoElement>,
  stream: MediaStream | undefined,
  options: VideoCalibrationOptions
) {
  const [attempt, setAttempt] = useState(0);
  const [items, setItems] = useState<CalibrationItem[]>([]);
  const previousPixels = useRef<Uint8ClampedArray>();
  const stableSince = useRef<number>();
  const usableFaceSince = useRef<number>();
  const obstructionSince = useRef<number>();
  const analyzingFace = useRef(false);

  const retry = useCallback(() => {
    previousPixels.current = undefined;
    stableSince.current = undefined;
    usableFaceSince.current = undefined;
    obstructionSince.current = undefined;
    setAttempt((value) => value + 1);
  }, []);

  useEffect(() => {
    const required = options.required ?? true;
    if (!required) {
      setItems([
        { key: 'secondary', label: 'Secondary camera', mandatory: false, state: 'not_required', guidance: 'A phone camera is not required for this session.' }
      ]);
      return;
    }

    const config = options.mode === 'mobile' ? calibrationConfig.mobile : calibrationConfig.laptop;
    const base = options.mode === 'mobile'
      ? [
          item('camera', 'Camera active', true, 'Select a camera and start the preview.'),
          item('frames', 'Video clear', true, 'Wait for the preview or restart the camera.'),
          item('lighting', 'Lighting', true, 'Improve the lighting in front of the workspace.'),
          item('visibility', 'Camera visibility', true, 'Clean the lens and remove anything covering it.'),
          item('stability', 'Phone stability', true, 'Place the phone on a stable surface or stand.'),
          item('connection', 'Desktop connection', true, 'Connect the camera and keep this page open.')
        ]
      : [
          item('permission', 'Laptop camera permission', true, 'Allow camera access in browser settings.'),
          item('camera', 'Laptop camera', true, 'Reopen camera setup and restart the camera.'),
          item('frames', 'Video frames', true, 'Wait for the camera preview to become active.'),
          item('face', 'Face position', true, 'Keep your face centred and move slightly closer if needed.'),
          item('lighting', 'Lighting', true, 'Improve the lighting in front of you.'),
          item('visibility', 'Camera visibility', true, 'Remove anything covering the camera and clean the lens.'),
          item('microphone', 'Microphone', calibrationConfig.publicDemo.microphoneRequired, 'Allow microphone access in the browser.'),
          item('connection', 'Connection', true, 'Check your internet connection and retry.')
        ];
    setItems(base);

    const startedAt = Date.now();
    const detectorConstructor = (window as typeof window & { FaceDetector?: FaceDetectorConstructor }).FaceDetector;
    const detector = detectorConstructor && options.mode === 'laptop'
      ? new detectorConstructor({ fastMode: true, maxDetectedFaces: 1 })
      : undefined;
    let cancelled = false;

    const update = async () => {
      if (cancelled) return;
      const now = Date.now();
      const video = videoRef.current;
      const videoTrack = stream?.getVideoTracks()[0];
      const cameraLive = videoTrack?.readyState === 'live';
      const sample = video ? sampleVideoFrame(video) : undefined;
      const dimensionsReady = Boolean(
        video && sample && video.videoWidth >= config.minimumVideoWidth && video.videoHeight >= config.minimumVideoHeight
      );

      if (sample) {
        const difference = averageFrameDifference(previousPixels.current, sample.pixels);
        previousPixels.current = sample.pixels;
        if (options.mode === 'mobile') {
          if (difference <= calibrationConfig.mobile.stableFrameDifferenceThreshold) stableSince.current ??= now;
          else stableSince.current = undefined;
        }
        if (sample.obstructed) obstructionSince.current ??= now;
        else obstructionSince.current = undefined;
      }

      let faceUsable = options.mode !== 'laptop';
      if (options.mode === 'laptop' && video && sample && !analyzingFace.current) {
        if (detector) {
          analyzingFace.current = true;
          try {
            const faces = await detector.detect(video);
            const face = faces[0]?.boundingBox;
            const centred = Boolean(face && face.width >= video.videoWidth * 0.12 && face.width <= video.videoWidth * 0.8 && face.x + face.width / 2 >= video.videoWidth * 0.2 && face.x + face.width / 2 <= video.videoWidth * 0.8);
            if (centred) usableFaceSince.current ??= now;
            else usableFaceSince.current = undefined;
          } catch { usableFaceSince.current = undefined; }
          analyzingFace.current = false;
          faceUsable = Boolean(usableFaceSince.current && now - usableFaceSince.current >= calibrationConfig.laptop.faceStabilityMs);
        } else {
          usableFaceSince.current ??= now;
          faceUsable = now - usableFaceSince.current >= calibrationConfig.laptop.faceStabilityMs;
        }
      }

      setItems((current) => current.map((check) => {
        let passed = false;
        let attention = false;
        if (check.key === 'permission') passed = Boolean(stream);
        if (check.key === 'camera') passed = Boolean(cameraLive);
        if (check.key === 'frames') passed = dimensionsReady;
        if (check.key === 'face') passed = faceUsable;
        if (check.key === 'lighting') {
          passed = Boolean(sample && sample.brightness >= config.lowBrightnessThreshold && sample.brightness <= config.overexposureThreshold);
          attention = Boolean(sample && !passed);
        }
        if (check.key === 'visibility') {
          passed = Boolean(sample && (!obstructionSince.current || now - obstructionSince.current < config.obstructionConfirmationMs));
          attention = Boolean(sample && !passed);
        }
        if (check.key === 'stability') passed = Boolean(stableSince.current && now - stableSince.current >= calibrationConfig.mobile.stabilityDurationMs);
        if (check.key === 'microphone') passed = options.microphoneTrack?.readyState === 'live';
        if (check.key === 'connection') passed = options.mode === 'mobile' ? Boolean(options.connected) : navigator.onLine;
        const timeoutMs = check.key === 'connection' ? calibrationConfig.connectionReadinessTimeoutMs : config.cameraStartTimeoutMs;
        const timedOut = now - startedAt >= timeoutMs;
        return { ...check, state: passed ? 'passed' : attention ? 'needs_attention' : timedOut ? 'failed' : 'checking' };
      }));
    };

    void update();
    const timer = window.setInterval(() => void update(), config.frameSamplingIntervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      previousPixels.current = undefined;
    };
  }, [attempt, options.connected, options.microphoneTrack, options.mode, options.required, stream, videoRef]);

  const ready = useMemo(() => mandatoryCalibrationPassed(items), [items]);
  const preConnectionReady = useMemo(() => mandatoryCalibrationPassed(items, true), [items]);
  return { items, ready, preConnectionReady, retry };
}
